import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective } from '@jsverse/transloco';

import { Validators, FormBuilder, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { LoginError, LoginErrorCode } from '../../../core/auth/auth.models';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, TranslocoDirective],
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPageComponent implements OnInit {
  buttonConfig = {
    type: 'submit',
    text: {
      label: 'Login',
    },
    states: {
      isLoading: false,
      isDisabled: false,
    },
  };

  getButtonConfig(t: (key: string) => string) {
    return {
      ...this.buttonConfig,
      text: { label: t('login.actions.signIn') },
    };
  }

  loginError: LoginErrorCode | null = null;
  loading = false;
  showPassword = false;

  get hasLoginError(): boolean {
    return this.loginError !== null;
  }

  /** Clave del aviso, para que el copy dependa del motivo real del fallo. */
  get loginErrorMessageKey(): string {
    return `login.errors.${this.loginError ?? 'generic'}`;
  }

  form!: FormGroup;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.prepareForm();
  }

  prepareForm(): void {
    this.form = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      remember: [false],
    });

    // El error se quita en cuanto el usuario corrige cualquiera de los campos
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loginError = null;
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSignIn(): void {
    if (this.form.invalid) return;
    this.loading = true;
    this.loginError = null;

    const { username, password, remember } = this.form.value;
    this.auth.login({ username: username!, password: password!, remember: !!remember }).subscribe({
      next: () => this.router.navigate(['/reports']),
      error: (err: unknown) => {
        this.loading = false;
        this.loginError = err instanceof LoginError ? err.code : 'generic';
      },
    });
  }
}
